import { useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  CONSENT_MODES,
  EMAIL_ALIGNMENTS,
  type CampaignInput,
  type ConsentMode,
  type EmailAlign,
  type Vertical,
} from '@dmn/shared';
import { VerticalPicker } from '@/components/VerticalPicker';
import { PageBody, PageHeader } from '@/components/layout/PageHeader';
import { ErrorState } from '@/components/states';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { api, ApiError } from '@/lib/api';
import { queryKeys } from '@/lib/queries';
import { todayUtc } from '@/lib/utils';

/** "every day", "every 7 days", "every 12 hours" — not "every 168 hours". */
function everyInterval(hours: number): string {
  if (hours === 24) return 'every day';
  if (hours % 24 === 0) return `every ${String(hours / 24)} days`;
  if (hours === 1) return 'every hour';
  return `every ${String(hours)} hours`;
}

export const Route = createFileRoute('/campaigns/new')({ component: NewCampaign });

const DEFAULT_FLIGHT_DAYS = 60;

function addDaysUtc(days: number): string {
  const date = new Date();
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
}

function NewCampaign() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const sponsors = useQuery({ queryKey: queryKeys.sponsors, queryFn: api.sponsors.list });
  const placements = useQuery({ queryKey: queryKeys.placements, queryFn: api.placements.list });
  const locations = useQuery({ queryKey: queryKeys.locations, queryFn: api.locations.list });

  const [sponsorId, setSponsorId] = useState('');
  const [name, setName] = useState('');
  const [flightStart, setFlightStart] = useState(todayUtc());
  const [flightEnd, setFlightEnd] = useState(addDaysUtc(DEFAULT_FLIGHT_DAYS));
  const [verticals, setVerticals] = useState<Vertical[]>(['obgyn']);
  const [placementIds, setPlacementIds] = useState<string[]>([]);
  const [perPersonPerWindow, setPerPersonPerWindow] = useState(1);
  const [windowHours, setWindowHours] = useState(24);
  const [perPersonTotal, setPerPersonTotal] = useState(6);
  // 0 means uncapped — the campaign runs to its flight end.
  const [totalImpressions, setTotalImpressions] = useState(0);
  const [priority, setPriority] = useState(50);
  const [utmCampaign, setUtmCampaign] = useState('');
  // `center` is what the email template produced before this was configurable.
  const [emailAlign, setEmailAlign] = useState<EmailAlign>('center');
  // `inherit` is the placement's own rule — what every campaign did before this.
  const [consentMode, setConsentMode] = useState<ConsentMode>('inherit');
  // Empty is valid — it means "use the campaign id", the pre-existing behaviour.
  const utmValid = utmCampaign === '' || /^[a-zA-Z0-9_-]{1,50}$/.test(utmCampaign);

  // Both caps are per person, and which is which is the thing operators get wrong —
  // `totalImpressions` under Delivery is the campaign-wide one. Spelling the rule out
  // as a sentence is cheaper than expecting three numbers to convey it.
  const capSummary = (() => {
    const parts: string[] = [];
    if (perPersonPerWindow > 0 && windowHours > 0) {
      const n = perPersonPerWindow;
      parts.push(
        `at most ${String(n)} impression${n === 1 ? '' : 's'} ${everyInterval(windowHours)}`
      );
    }
    if (perPersonTotal > 0) parts.push(`${String(perPersonTotal)} in total, ever`);

    if (parts.length === 0) {
      return 'No frequency cap: one person could see this campaign every time they are eligible.';
    }
    return `Each person sees ${parts.join(', and no more than ')}. Set a field to 0 to drop that limit.`;
  })();

  const create = useMutation({
    mutationFn: (input: CampaignInput) => api.campaigns.create(input),
    onSuccess: async (campaign) => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.campaigns });
      await navigate({ to: '/campaigns/$campaignId', params: { campaignId: campaign.id } });
    },
  });

  const toggle = <T extends string>(list: T[], value: T): T[] =>
    list.includes(value) ? list.filter((entry) => entry !== value) : [...list, value];

  const activePlacements = (placements.data ?? []).filter((p) => p.status === 'active');
  const valid =
    sponsorId &&
    name.trim() &&
    verticals.length > 0 &&
    placementIds.length > 0 &&
    flightStart <= flightEnd &&
    utmValid;

  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    if (!valid) return;
    create.mutate({
      sponsorId,
      name: name.trim(),
      flightStart,
      flightEnd,
      verticals,
      eligibleLocationIds: [],
      excludedLocationIds: [],
      placementIds,
      caps: { perPersonPerWindow, windowHours, perPersonTotal, totalImpressions },
      utmCampaign: utmCampaign.trim() || null,
      emailAlign,
      consentMode,
      // One model today, so this is not a form field — but send it explicitly
      // rather than leaning on the schema default, so a new campaign's document
      // carries the value and the audit trail can see it change later.
      distributionModel: 'random',
      // Always off at creation. Sharing ZIPs is switched on afterwards, on the
      // campaign page, as its own audited step.
      utmZip: false,
      priority,
    });
  };

  if (sponsors.isError) return <ErrorState error={sponsors.error} />;

  return (
    <>
      <PageHeader title="New campaign" description="Campaigns are created as drafts" />
      <PageBody>
        <form onSubmit={submit} className="max-w-3xl space-y-6">
          <Card className="shadow-none">
            <CardHeader>
              <CardTitle className="text-sm font-medium">Basics</CardTitle>
            </CardHeader>
            <CardContent className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="sponsor">Sponsor</Label>
                <Select value={sponsorId} onValueChange={setSponsorId}>
                  <SelectTrigger id="sponsor">
                    <SelectValue placeholder="Select a sponsor" />
                  </SelectTrigger>
                  <SelectContent>
                    {(sponsors.data ?? [])
                      .filter((sponsor) => sponsor.status === 'active')
                      .map((sponsor) => (
                        <SelectItem key={sponsor.id} value={sponsor.id}>
                          {sponsor.name}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="name">Campaign name</Label>
                <Input
                  id="name"
                  value={name}
                  onChange={(event) => setName(event.target.value)}
                  placeholder="Clear Future — OB/GYN pilot (Q3)"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="flight-start">Flight start (UTC)</Label>
                <Input
                  id="flight-start"
                  type="date"
                  value={flightStart}
                  onChange={(event) => setFlightStart(event.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="flight-end">Flight end (UTC)</Label>
                <Input
                  id="flight-end"
                  type="date"
                  value={flightEnd}
                  onChange={(event) => setFlightEnd(event.target.value)}
                />
                {flightStart > flightEnd && (
                  <p className="text-xs text-destructive">End must be on or after start.</p>
                )}
              </div>
            </CardContent>
          </Card>

          <Card className="shadow-none">
            <CardHeader>
              <CardTitle className="text-sm font-medium">Targeting</CardTitle>
              <CardDescription>
                Context only — vertical, location and placement. There is no field here for clinical
                content, by design.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-5">
              <fieldset className="space-y-2">
                <legend className="text-sm font-medium">Verticals</legend>
                <VerticalPicker
                  value={verticals}
                  onChange={setVerticals}
                  locations={locations.data ?? []}
                  locationsLoading={locations.isPending}
                />
              </fieldset>

              <fieldset className="space-y-2">
                <legend className="text-sm font-medium">Placements</legend>
                <div className="grid gap-2 pt-1 sm:grid-cols-2">
                  {activePlacements.map((placement) => (
                    <label
                      key={placement.id}
                      className="flex items-start gap-2 rounded-md border p-2 text-sm hover:bg-accent"
                    >
                      <Checkbox
                        className="mt-0.5"
                        checked={placementIds.includes(placement.id)}
                        onCheckedChange={() =>
                          setPlacementIds((prev) => toggle(prev, placement.id))
                        }
                      />
                      <span className="min-w-0">
                        <span className="block truncate font-medium">{placement.name}</span>
                        <span className="block font-mono text-xs text-muted-foreground">
                          {placement.id}
                        </span>
                      </span>
                    </label>
                  ))}
                </div>
                {placements.isSuccess && activePlacements.length === 0 && (
                  <p className="text-sm text-muted-foreground">
                    No active placements — register one under Placements first.
                  </p>
                )}
              </fieldset>
            </CardContent>
          </Card>

          <Card className="shadow-none">
            <CardHeader>
              <CardTitle className="text-sm font-medium">Frequency caps</CardTitle>
              <CardDescription>
                How often one person sees this campaign. These protect the patient experience — the
                fastest way to make a sponsored unit resented is to repeat it.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 sm:grid-cols-3">
                <div className="space-y-2">
                  <Label htmlFor="cap-window">Impressions per window</Label>
                  <Input
                    id="cap-window"
                    type="number"
                    min={0}
                    value={perPersonPerWindow}
                    onChange={(event) => setPerPersonPerWindow(Number(event.target.value))}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="cap-hours">Window (hours)</Label>
                  <Input
                    id="cap-hours"
                    type="number"
                    min={1}
                    value={windowHours}
                    onChange={(event) => setWindowHours(Number(event.target.value))}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="cap-total">Lifetime per person</Label>
                  <Input
                    id="cap-total"
                    type="number"
                    min={0}
                    value={perPersonTotal}
                    onChange={(event) => setPerPersonTotal(Number(event.target.value))}
                  />
                </div>
              </div>

              {/* A sentence rather than three labels, because the fields only mean
                  something together and "1 / 24 / 6" reads as nothing. */}
              <p className="rounded-md bg-muted px-3 py-2 text-xs text-muted-foreground">
                {capSummary}
              </p>
            </CardContent>
          </Card>

          <Card className="shadow-none">
            <CardHeader>
              <CardTitle className="text-sm font-medium">Delivery</CardTitle>
              <CardDescription>
                How much the campaign delivers overall, and what happens when it competes with
                another campaign for the same slot.
              </CardDescription>
            </CardHeader>
            <CardContent className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="cap-impressions">Total impressions</Label>
                <Input
                  id="cap-impressions"
                  type="number"
                  min={0}
                  value={totalImpressions}
                  onChange={(event) => setTotalImpressions(Number(event.target.value))}
                />
                <p className="text-xs text-muted-foreground">
                  A ceiling for the whole campaign, across every person — what the sponsor bought. 0
                  leaves it uncapped, running to the flight end. Delivery can finish slightly over:
                  an impression is reported a moment after it happens.
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="priority">Priority</Label>
                <Input
                  id="priority"
                  type="number"
                  min={0}
                  max={100}
                  value={priority}
                  onChange={(event) => setPriority(Number(event.target.value))}
                />
                <p className="text-xs text-muted-foreground">
                  0–100, higher wins when more than one campaign is eligible for a slot. 50 is the
                  default; house ads sit near 10 so they only fill what nobody paid for.
                </p>
              </div>
            </CardContent>
          </Card>

          <Card className="shadow-none">
            <CardHeader>
              <CardTitle className="text-sm font-medium">Attribution</CardTitle>
              <CardDescription>
                What a sponsor sees in their own analytics when someone clicks through.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="max-w-sm space-y-2">
                <Label htmlFor="utm-campaign">utm_campaign</Label>
                <Input
                  id="utm-campaign"
                  value={utmCampaign}
                  placeholder="cu1"
                  className="font-mono"
                  onChange={(event) => setUtmCampaign(event.target.value.trim())}
                />
                <p className="text-xs text-muted-foreground">
                  Added to every sponsor link from this campaign, alongside{' '}
                  <code className="font-mono">utm_source=deardoc</code> and{' '}
                  <code className="font-mono">utm_medium=partnership</code>, which are the same on
                  every link DMN produces. Leave empty to use the campaign id.
                </p>
                {!utmValid && (
                  <p className="text-xs text-destructive">
                    Letters, numbers, dash and underscore only.
                  </p>
                )}
              </div>
            </CardContent>
          </Card>

          <Card className="shadow-none">
            <CardHeader>
              <CardTitle className="text-sm font-medium">Consent</CardTitle>
              <CardDescription>
                Whether this campaign needs the person to have opted in. The placement already
                decides this for most campaigns; change it only when this one differs.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="max-w-sm space-y-2">
                <Label htmlFor="consent-mode">Consent mode</Label>
                <Select
                  value={consentMode}
                  onValueChange={(next) => setConsentMode(next as ConsentMode)}
                >
                  <SelectTrigger id="consent-mode">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CONSENT_MODES.map((mode) => (
                      <SelectItem key={mode} value={mode}>
                        {mode}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  <code className="font-mono">inherit</code> follows the placement — patient
                  surfaces ask, practice surfaces do not.{' '}
                  <code className="font-mono">required</code> asks even where the placement waives
                  it. <code className="font-mono">exempt</code> serves people who have not answered.
                  Practice surfaces are never asked whatever this says, and anyone who declined or
                  revoked is excluded either way.
                </p>
                {consentMode === 'exempt' && (
                  <p className="rounded-md bg-amber-500/10 px-3 py-2 text-xs text-amber-700 dark:text-amber-500">
                    This campaign will serve to patients who have never opted in. Anyone who
                    declined or revoked is still excluded — waiving removes the need to say yes,
                    never the effect of saying no.
                  </p>
                )}
              </div>
            </CardContent>
          </Card>

          <Card className="shadow-none">
            <CardHeader>
              <CardTitle className="text-sm font-medium">Email layout</CardTitle>
              <CardDescription>
                Where the unit sits in the host message. Only affects email placements — the web
                unit fills whatever slot the host product gives it.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="max-w-sm space-y-2">
                <Label htmlFor="email-align">Alignment</Label>
                <Select
                  value={emailAlign}
                  onValueChange={(next) => setEmailAlign(next as EmailAlign)}
                >
                  <SelectTrigger id="email-align">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {EMAIL_ALIGNMENTS.map((option) => (
                      <SelectItem key={option} value={option}>
                        {option}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  The unit is 600px wide at most, so in a wider message the spare room has to go
                  somewhere. <code className="font-mono">left</code> matches ordinary body copy;
                  <code className="font-mono"> center</code> is the old behaviour and can read as an
                  odd float beside left-aligned text.
                </p>
              </div>
            </CardContent>
          </Card>

          {create.isError && (
            <p className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
              {create.error instanceof ApiError
                ? create.error.message
                : 'Could not create the campaign'}
            </p>
          )}

          <div className="flex items-center gap-2">
            <Button type="submit" disabled={!valid || create.isPending}>
              {create.isPending ? 'Creating…' : 'Create draft'}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => void navigate({ to: '/campaigns' })}
            >
              Cancel
            </Button>
          </div>
        </form>
      </PageBody>
    </>
  );
}
